import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldAlert, HardHat, FlaskConical, MessagesSquare, Users, GraduationCap, ClipboardCheck, Siren, KeyRound, CalendarDays, FileText, Clock, AlertTriangle, CheckCircle2, Coins, ChevronUp, ChevronDown, Settings2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, Stat, StatusBadge, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";

const ACTIONS = [
  ["Generate Document", "/app/generate", Sparkles], ["Create Risk Assessment", "/app/generate?cat=risk-assessments", ShieldAlert], ["Create RAMS", "/app/generate?cat=rams", HardHat],
  ["Create COSHH", "/app/generate?cat=coshh", FlaskConical], ["Create Toolbox Talk", "/app/generate?cat=toolbox-talks", MessagesSquare], ["Manage Employees", "/app/team", Users],
  ["Assign Training", "/app/training", GraduationCap], ["Start Inspection", "/app/inspections", ClipboardCheck], ["Report Incident", "/app/incidents/new", Siren], ["Issue Permit", "/app/permits", KeyRound], ["Compliance Calendar", null, CalendarDays],
];
const COLORS = ["#00B894", "#FFA000", "#FF3131", "#3B82F6", "#64748B", "#0C1427"];
const DEFAULT_ORDER = ["stats", "actions", "charts", "lists"];

const Card = ({ title, children, className = "" }) => <div className={`rounded-2xl border bg-card p-6 ${className}`}><h3 className="mb-4 text-sm font-semibold">{title}</h3>{children}</div>;

const Stats = ({ d }) => (
  <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
    <Stat testid="stat-documents" label="Documents" value={d.total} hint={`${d.counts.draft || 0} drafts`} icon={FileText} />
    <Stat testid="stat-review" label="Awaiting review" value={d.counts.under_review || 0} tone="amber" hint="Submitted for approval" icon={Clock} />
    <Stat testid="stat-overdue" label="Reviews overdue" value={d.review_overdue} tone={d.review_overdue ? "red" : "green"} hint={`${d.review_due_30} due in 30 days`} icon={AlertTriangle} />
    <Stat testid="stat-credits" label="AI credits" value={d.balance} tone="green" hint={`${d.credit_monthly} monthly · ${d.credit_purchased} purchased`} icon={Coins} />
  </div>
);

const Actions = () => (
  <Card title="Quick actions">
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
      {ACTIONS.map(([l, to, Icon]) => to ? (
        <Link key={l} to={to} data-testid={`quick-${l.toLowerCase().replace(/\s/g, "-")}`} className="card-lift flex flex-col gap-3 rounded-xl border p-4 text-sm font-medium hover:border-health/50"><Icon className="h-5 w-5 text-health" />{l}</Link>
      ) : (
        <div key={l} className="flex flex-col gap-3 rounded-xl border border-dashed p-4 text-sm text-muted-foreground"><Icon className="h-5 w-5" />{l}<span className="font-mono text-[10px] uppercase">Coming soon</span></div>
      ))}
    </div>
  </Card>
);

const Charts = ({ d }) => (
  <div className="grid gap-4 lg:grid-cols-3">
    <Card title="Review currency" className="lg:col-span-1">
      <p className="font-display text-5xl font-semibold" data-testid="review-currency">{d.review_currency.pct == null ? "—" : `${d.review_currency.pct}%`}</p>
      <p className="mt-1 text-sm text-muted-foreground">{d.review_currency.in_date} of {d.review_currency.total} live documents in date</p>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted"><div className={`h-full ${d.review_currency.pct >= 90 ? "bg-health" : d.review_currency.pct >= 70 ? "bg-[#FFA000]" : "bg-alert"}`} style={{ width: `${d.review_currency.pct || 0}%` }} /></div>
      <p className="mt-4 text-xs text-muted-foreground">{d.review_currency.basis}</p>
    </Card>
    <Card title="AI generations by month" className="lg:col-span-1">
      {d.trend.length ? (
        <ResponsiveContainer width="100%" height={160}><AreaChart data={d.trend}><XAxis dataKey="month" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} width={24} /><Tooltip /><Area dataKey="generations" stroke="#00B894" fill="#00B894" fillOpacity={0.15} /></AreaChart></ResponsiveContainer>
      ) : <p className="text-sm text-muted-foreground">No generations yet.</p>}
    </Card>
    <Card title="Documents by category" className="lg:col-span-1">
      {d.categories.length ? (
        <div className="flex items-center gap-4"><ResponsiveContainer width="50%" height={150}><PieChart><Pie data={d.categories} dataKey="value" innerRadius={38} outerRadius={64}>{d.categories.map((_, i) => <Cell key={i} fill={COLORS[i % 6]} />)}</Pie></PieChart></ResponsiveContainer>
          <ul className="space-y-1 text-xs">{d.categories.map((c, i) => <li key={c.name} className="flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % 6] }} />{c.name} ({c.value})</li>)}</ul></div>
      ) : <p className="text-sm text-muted-foreground">No documents yet.</p>}
    </Card>
  </div>
);

const Lists = ({ d }) => (
  <div className="grid gap-4 lg:grid-cols-2">
    <Card title="Recent documents">
      {d.recent.length === 0 ? <EmptyState icon={Sparkles} title="Create your first document" text="Describe a job and Saflr will draft it." action={<Button asChild className="rounded-full"><Link to="/app/generate">Create with AI</Link></Button>} /> : (
        <ul className="divide-y">{d.recent.map((x) => (
          <li key={x.id}><Link to={`/app/documents/${x.id}`} className="flex items-center justify-between gap-3 py-3 hover:opacity-80"><div className="min-w-0"><p className="truncate text-sm font-medium">{x.title}</p><p className="font-mono text-xs text-muted-foreground">{x.doc_number} · v{x.version}</p></div><StatusBadge status={x.status} /></Link></li>
        ))}</ul>
      )}
    </Card>
    <Card title="Upcoming reviews & acknowledgements">
      <p className="mb-3 text-sm text-muted-foreground"><CheckCircle2 className="mr-1 inline h-4 w-4 text-health" />{d.pending_my_ack} awaiting your acknowledgement · {d.ack_outstanding} outstanding across team</p>
      {d.upcoming_reviews.length === 0 ? <p className="text-sm text-muted-foreground">No approved documents with review dates yet.</p> : (
        <ul className="divide-y">{d.upcoming_reviews.map((x) => (
          <li key={x.id}><Link to={`/app/documents/${x.id}`} className="flex justify-between py-3 text-sm"><span className="truncate">{x.title}</span><span className={`font-mono text-xs ${x.review_date < new Date().toISOString().slice(0, 10) ? "text-alert" : "text-muted-foreground"}`}>{x.review_date}</span></Link></li>
        ))}</ul>
      )}
    </Card>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [order, setOrder] = useState(() => JSON.parse(localStorage.getItem("saflr-widgets") || "null") || DEFAULT_ORDER);
  const [edit, setEdit] = useState(false);
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data));
  }, []);
  useEffect(() => {
    localStorage.setItem("saflr-widgets", JSON.stringify(order));
  }, [order]);
  if (!d) return <Loader label="Loading Command Centre" />;
  const move = (i, dir) => { const o = [...order]; [o[i], o[i + dir]] = [o[i + dir], o[i]]; setOrder(o); };
  const W = { stats: <Stats d={d} />, actions: <Actions />, charts: <Charts d={d} />, lists: <Lists d={d} /> };
  const sub = d.subscription;
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">HSE Command Centre</p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Good to see you, {user.name.split(" ")[0]}.</h1>
          {sub && <p className="mt-1 text-sm text-muted-foreground">{sub.plan_name} plan · {sub.status.replace("_", " ")} until {sub.period_end?.slice(0, 10)}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEdit(!edit)} data-testid="customise-dashboard"><Settings2 className="mr-1.5 h-4 w-4" />{edit ? "Done" : "Customise"}</Button>
          <Button asChild className="rounded-full"><Link to="/app/generate" data-testid="dashboard-create-ai"><Sparkles className="mr-1.5 h-4 w-4" />Create with AI</Link></Button>
        </div>
      </div>
      {order.map((k, i) => (
        <div key={k} className="relative fade-up" style={{ animationDelay: `${i * 80}ms` }}>
          {edit && <div className="absolute -left-1 top-2 z-10 flex flex-col gap-1 sm:-left-10">
            <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded border bg-card p-1 disabled:opacity-30" data-testid={`widget-up-${k}`}><ChevronUp className="h-4 w-4" /></button>
            <button disabled={i === order.length - 1} onClick={() => move(i, 1)} className="rounded border bg-card p-1 disabled:opacity-30" data-testid={`widget-down-${k}`}><ChevronDown className="h-4 w-4" /></button>
          </div>}
          {W[k]}
        </div>
      ))}
    </div>
  );
}
